import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import Label from '../Label';

export default class TextArea extends React.PureComponent {
    static propTypes = {
        /** html id required to connect the input with the label */
        id: PropTypes.string.isRequired,

        /** Label contents, usually text */
        label: PropTypes.node.isRequired,

        /** If false, the label will be screen reader only */
        isLabelVisible: PropTypes.bool,

        /** Text to be used for the input placeholder */
        placeholder: PropTypes.string,

        /** The input's value. Use only when the input is controlled */
        value: PropTypes.string,

        /** Disables the field */
        isDisabled: PropTypes.bool,

        /** Marks the field as required; Adds required styling to label */
        isRequired: PropTypes.bool,

        /** Marks and styles the field as invalid */
        isInvalid: PropTypes.bool,

        /** Contents of the error message, usually text */
        errorText: PropTypes.node,

        /** Ref to be passed to the input */
        innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.node]),

        /** If true, the input will gain focus when it mounts */
        shouldFocusOnMount: PropTypes.bool,

        /** Additional className added to outer container for custom styling */
        className: PropTypes.string
    };

    static defaultProps = {
        isDisabled: false,
        isLabelVisible: true,
        value: undefined,
        placeholder: undefined,
        isRequired: false,
        isInvalid: false,
        errorText: undefined,
        innerRef: undefined,
        className: undefined
    };

    constructor(props) {
        super(props);

        this.inputWrapperRef = React.createRef();
    }

    componentDidMount() {
        const { shouldFocusOnMount } = this.props;

        this.timeout = setTimeout(() => {
            if (shouldFocusOnMount && this.inputWrapperRef) {
                this.inputWrapperRef.current.querySelector('textarea').focus();
            }
        }, 0);
    }

    componentWillUnmount() {
        // Safety in case of render thrashing
        clearTimeout(this.timeout);
    }

    render() {
        const {
            id,
            label,
            isDisabled,
            value,
            isLabelVisible,
            placeholder,
            isRequired,
            isInvalid,
            errorText,
            innerRef,
            className,
            shouldFocusOnMount,
            ...rest
        } = this.props;

        return (
            <div
                data-component="TextArea"
                className={cx('sarsa-field-outer-container', className)}>
                <Label
                    htmlFor={id}
                    isVisible={isLabelVisible}
                    isRequired={isRequired}>
                    {label}
                </Label>
                <div
                    className="sarsa-field-inner-container"
                    ref={this.inputWrapperRef}>

                    {errorText && (
                        <div
                            id={`${id}-error`}
                            className="sarsa-field-error-message">
                            <span className="req" aria-hidden="true">*</span>
                            {errorText}
                        </div>
                    )}

                    <div className="sarsa-field-input-wrapper">
                        <textarea
                            className={cx('sarsa-text-field-input', {
                                'is-invalid': isInvalid
                            })}
                            id={id}
                            ref={innerRef}
                            disabled={isDisabled}
                            value={value}
                            placeholder={placeholder}
                            required={isRequired}
                            aria-invalid={isInvalid}
                            aria-describedby={errorText ? `${id}-error` : null}
                            {...rest} />
                    </div>
                </div>
            </div>
        );
    }
}